import React, { FC, useEffect, useState } from 'react';
import _ from 'lodash';
import { ReportsNestedTableCont } from './ReportsNestedGroupTable.styles';
import { pagingInfoMock } from '../../../../__mocks__/surveysData';
import { useFilters } from '../../../../hooks/useFilters';
import { useReports } from '../../../../hooks/useReports';
import { IItemReportResponse, IReportFilters } from '../../../../services/reports';
import { IPagingInfo } from '../../../../services/surveys';
import MembersTableFooter from '../../../ParticipantsTable/ParticipantsTableTile/MembersTableFooter/MembersTableFooter';
import ReportsNestedTableBody from '../ReportsNestedTableBody/ReportsNestedTableBody';
import ReportsNestedTableHeader from '../ReportsNestedTableHeader/ReportsNestedTableHeader';

interface IReportsNestedTable {
  reportId: string;
  areReportsAvailable: boolean;
  initialItems?: IItemReportResponse[];
  metadataFromHigherReq?: IPagingInfo;
  surveyType: string;
}

const ReportsNestedTable: FC<IReportsNestedTable> = ({
  reportId,
  areReportsAvailable,
  initialItems,
  metadataFromHigherReq,
  surveyType,
}) => {
  const [prevSearch, setPrevSearch] = useState<string | undefined>(undefined);
  const [metadata, setMetadata] = useState<IPagingInfo>(metadataFromHigherReq || pagingInfoMock);
  const [items, setItems] = useState<IItemReportResponse[]>(initialItems || []);
  const [loading, setLoading] = useState<boolean>(false);
  const { applyNestedFilter, getNestedFilters, currentFilters } = useFilters();
  const { downloadReport } = useReports();
  const reportsAllFilters = getNestedFilters('reports');
  const thisReportFilter: IReportFilters = reportsAllFilters[reportId] || {};
  const { search, perPage, pageNo, sortOrder } = thisReportFilter;

  const normalizeNestedFilters = (filters: Record<string, string | number | null | undefined>) =>
    Object.entries(filters).reduce<Record<string, string | null>>((acc, [key, value]) => {
      acc[key] = value != null ? String(value) : null;
      return acc;
    }, {});

  const processReports = async () => {
    setLoading(true);
    try {
      const reportsResponse = await downloadReport(reportId, thisReportFilter, areReportsAvailable);
      const { items: downloadedItems, pagingInfo } = reportsResponse;
      setMetadata(pagingInfo);
      setItems(downloadedItems);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentFilters?.search !== prevSearch) {
      if (metadataFromHigherReq) {
        setMetadata(metadataFromHigherReq);
      }
      if (metadata.pageNo === 1 && initialItems) {
        setItems(initialItems);
      }
    }
    (async () => processReports())();
    const thisSearch = typeof currentFilters?.search === 'string' ? currentFilters.search : undefined;
    setPrevSearch(thisSearch);
  }, [reportId, search, perPage, pageNo, sortOrder, initialItems, currentFilters?.search]);

  return (
    <ReportsNestedTableCont id="ReportsNestedTableCont">
      <ReportsNestedTableHeader id={reportId} />
      <ReportsNestedTableBody items={items} surveyType={surveyType} loading={loading} />
      <MembersTableFooter
        pagingInfo={metadata}
        applyFilters={(filters) =>
          applyNestedFilter(normalizeNestedFilters(filters), reportId, 'reports')
        }
      />
    </ReportsNestedTableCont>
  );
};

export default ReportsNestedTable;
